import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import type { GetUserWatchlistsResponse } from "@foundation/watchlist-client";
import { useRegUIClient } from "@foundation/reg-ui-client";
import { DropdownOption } from "@bloomberg/media-ui-dropdown/types";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { useFeatureGate } from "@foundation/statsig";
import styles from "./watchlistWrapped.module.css";
import { WrappedHeaderSection } from "./headerSection";
import { WrappedContentSection } from "./contentSection";
import { fetchPortfolioData } from "~api/portfolio/portfolioData";
import { LOADING_STATE } from "~constants/Portfolio";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import { WrappedWatchlist } from "~types/octagon";
import { getWrappedWatchlist } from "~api/fetchWatchlistWrapped";
import WatchlistBundleContainer from "~components/WatchlistBundleContainer";
import { savePortfolio } from "~utils/portfolio/createPortfolio";
import { usePageData } from "~context/PageContext";
import { WRAPPED_WATCHLIST_STORAGE_KEY } from "~constants/pagePreference";
import { WatchlistWrappedPlaceholder } from "~components/WatchlistWrapped/WatchlistWrappedPlaceholder";
import { logError } from "~utils/newrelic/newrelicBrowser";

type WatchlistWrappedProps = {
  onBgChange?: (shouldApplyEmptyStateBg: boolean) => void;
};

export function WatchlistWrapped({ onBgChange }: WatchlistWrappedProps) {
  const [watchlistLoadingState, setWatchlistLoadingState] = useState(
    LOADING_STATE.INITIAL
  );
  const [wrappedLoadingState, setWrappedLoadingState] = useState(
    LOADING_STATE.INITIAL
  );
  const [wrappedWatchlist, setWrappedWatchlist] = useState<WrappedWatchlist>();
  const [watchlistOptions, setWatchlistOptions] = useState<DropdownOption[]>(
    []
  );
  const [selectedWatchlistOption, setSelectedWatchlistOption] =
    useState<DropdownOption>();

  const hasRefreshed = useRef(false);

  const { session } = useRegUIClient();
  const bconnectId = session?.userInfo?.userId ?? "";

  const { selectedPortfolio, upsertPortfolio, updateSelectedPortfolio } =
    usePortfolioPage();

  const { pagePreference, updatePagePreference } = usePageData();

  const persistedWrappedWatchlistId = useMemo(() => {
    return pagePreference[WRAPPED_WATCHLIST_STORAGE_KEY] || null;
  }, [pagePreference]);

  const featureGateEnabled = Boolean(
    useFeatureGate("watchlist_paywall")?.value ?? false
  );

  const handleWatchlistChange = useCallback(
    (option: DropdownOption) => {
      const watchlistId = option.value;

      setSelectedWatchlistOption(option);
      updatePagePreference(WRAPPED_WATCHLIST_STORAGE_KEY, watchlistId);
      setWrappedLoadingState(LOADING_STATE.LOADING);

      getWrappedWatchlist(bconnectId, watchlistId)
        .then((res) => {
          setWrappedWatchlist(res);
          setWrappedLoadingState(LOADING_STATE.COMPLETE);
        })
        .catch((err) => {
          console.error("Failed to fetch wrapped watchlist", err);
          setWrappedLoadingState(LOADING_STATE.FAILED);
          logError(new Error(`Watchlist Wrapped Fetch Failed`), {
            errorMessage: JSON.stringify(err),
          });
        });
    },
    [bconnectId, getWrappedWatchlist]
  );

  const refreshWrappedWatchlist = useCallback(() => {
    if (!selectedWatchlistOption) return;

    setWrappedLoadingState(LOADING_STATE.LOADING);

    getWrappedWatchlist(bconnectId, selectedWatchlistOption.value)
      .then((res) => {
        setWrappedWatchlist(res);
        setWrappedLoadingState(LOADING_STATE.COMPLETE);
      })
      .catch((err) => {
        console.error("Failed to refresh wrapped watchlist", err);
        setWrappedLoadingState(LOADING_STATE.FAILED);
        logError(new Error(`Watchlist Wrapped Refresh Failed`), {
          errorMessage: JSON.stringify(err),
        });
      });
  }, [bconnectId, selectedWatchlistOption, getWrappedWatchlist]);

  const refreshPortfolios = useCallback(
    (persistedIdOverride?: string) => {
      if (!bconnectId) {
        logError(new Error(`Watchlist Wrapped BconnectId is null`));

        return;
      }

      setWatchlistLoadingState(LOADING_STATE.LOADING);

      fetchPortfolioData({ bconnectId })
        .then((result) => {
          const { portInfos, port } = result.data as GetUserWatchlistsResponse;

          updateSelectedPortfolio(port);

          const options = portInfos.map((info) => ({
            value: String(info.id),
            displayValue: info.name || "Unnamed Watchlist",
            isDefault: info.isDefault,
          }));

          setWatchlistOptions(options);
          const persistedId =
            persistedIdOverride ?? persistedWrappedWatchlistId;
          const persitedOption = persistedId
            ? options.find((o) => o.value === persistedId)
            : undefined;
          const defaultOption =
            persitedOption || options.find((o) => o.isDefault);

          if (defaultOption) {
            setSelectedWatchlistOption(defaultOption);
            handleWatchlistChange(defaultOption);
          }

          setWatchlistLoadingState(LOADING_STATE.COMPLETE);
        })
        .catch((error) => {
          console.error("Portfolio fetch error", error);
          setWatchlistLoadingState(
            error.status === 404 ? LOADING_STATE.EMPTY : LOADING_STATE.FAILED
          );
          logError(new Error(`Watchlist Wrapped Portfolio Fetch Error`), {
            errorMessage: JSON.stringify(error),
          });
        });
    },
    [bconnectId, handleWatchlistChange, updateSelectedPortfolio]
  );

  // When a user logs in, either create a new bundle or refresh their portfolio
  useEffect(() => {
    if (session?.isRegUIClientLoaded && !session?.isLoggedIn) {
      return;
    }

    const bundleIntentRaw = window.sessionStorage.getItem(
      "createBundleAfterLogin"
    );
    const bundleIntent = bundleIntentRaw ? JSON.parse(bundleIntentRaw) : null;

    // Create bundle after login
    if (bundleIntent?.shouldCreate) {
      setWatchlistLoadingState(LOADING_STATE.LOADING);
      savePortfolio(bundleIntent.bundlePortfolio, bconnectId)
        .then((portfolio) => {
          upsertPortfolio(portfolio);
          setWatchlistLoadingState(LOADING_STATE.COMPLETE);

          const newWatchlistID = String(portfolio.portInfo.id);

          updatePagePreference(WRAPPED_WATCHLIST_STORAGE_KEY, newWatchlistID);
          refreshPortfolios(newWatchlistID);
        })
        .catch((e) => {
          setWatchlistLoadingState(LOADING_STATE.FAILED);
          console.error("[BUNDLE] Error Saving", e);
          logError(new Error(`Watchlist Wrapped Bundle Save Error`), {
            errorMessage: JSON.stringify(e),
          });
        });

      window.sessionStorage.removeItem("createBundleAfterLogin");
    } else {
      refreshPortfolios();
    }
  }, [session?.isLoggedIn, session?.isRegUIClientLoaded]);

  useEffect(() => {
    if (!session?.isRegUIClientLoaded) return;

    const shouldApplyEmptyStateBg =
      !session?.isLoggedIn || watchlistLoadingState === LOADING_STATE.EMPTY;

    onBgChange?.(shouldApplyEmptyStateBg);
  }, [
    session?.isRegUIClientLoaded,
    session?.isLoggedIn,
    watchlistLoadingState,
    onBgChange,
  ]);

  // Update component if a user had no watchlist but created one from the bundles
  useEffect(() => {
    if (
      watchlistLoadingState === LOADING_STATE.EMPTY &&
      selectedPortfolio &&
      !hasRefreshed.current
    ) {
      hasRefreshed.current = true;
      refreshPortfolios();
    }
  }, [selectedPortfolio, watchlistLoadingState, refreshPortfolios]);

  const sessionLoading = session?.isRegUIClientLoaded !== true;

  if (
    sessionLoading ||
    wrappedLoadingState === LOADING_STATE.LOADING ||
    watchlistLoadingState === LOADING_STATE.LOADING
  ) {
    return <WatchlistWrappedPlaceholder />;
  }

  // Show bundles if user is signed out or if they do not have a watchlist.
  if (
    !session?.isLoggedIn ||
    (featureGateEnabled && !session?.isBBGSubscriber) ||
    watchlistLoadingState === LOADING_STATE.EMPTY
  ) {
    // Fires when a user doesn't have a watchlist, so we can assume they see bundles
    trackClickEvent(
      "watchlistPresence",
      "user_without_watchlist",
      "watchlist",
      "uiaction"
    );

    return <WatchlistBundleContainer variant="wrapped" />;
  }

  // Fires when a user has a watchlist in general, so they see the happy path
  // for watchlist wrapped module instead of the bundles
  trackClickEvent(
    "watchlistPresence",
    "user_with_watchlist",
    "watchlist",
    "uiaction"
  );

  return (
    <div className={styles.root}>
      <WrappedHeaderSection
        loadingState={watchlistLoadingState}
        watchlistOptions={watchlistOptions}
        selectedWatchlistOption={selectedWatchlistOption}
        onWatchlistChange={handleWatchlistChange}
        onRefresh={refreshPortfolios}
      />
      {watchlistLoadingState !== LOADING_STATE.FAILED && (
        <WrappedContentSection
          loadingState={wrappedLoadingState}
          wrappedWatchlist={wrappedWatchlist}
          onRetry={refreshWrappedWatchlist}
        />
      )}
    </div>
  );
}
